import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { RagAlert, RagImageItem, RagProcedure, RagVideoItem } from "../../types/ragChatTypes";
import {
  attachProcedureStepVideos,
  bindProcedureClipsToNumberedItems,
  buildStepImagesByIndex,
  cleanStepDisplayText,
  classifyNumberedAnswerKind,
  documentFromProcedurePayload,
  extractProcedureIntroProse,
  extractStepActionLink,
  extractStepVideoReferences,
  extractVideoPrimaryProcedureNotice,
  getAlertFallbackCompactBlocks,
  groupProcedureSections,
  isVideoPrimaryProcedureNotice,
  parseNumberedSteps,
  parseProcedureDocumentWithAlerts,
  parseSegments,
  procedureBlocksForDisplay,
  procedurePayloadIsLayoutSource,
  splitProcedureBlocksForDisplay,
  shouldShowStandaloneProcedureStepHeading,
  shouldUseProcedureStepCards,
  splitStepTitleBody,
  splitVideoSourceProvenanceNotice,
  stripInlineMarkdown,
  stripDisplayedVideoCitations,
  stripStepVideoReferences,
  type ParsedStep,
  type ProcedureBlock,
  type ProcedureBlockItem,
  type ProcedureSection,
  type StepImageBinding,
} from "../../utils/ragChatUtils";
import { evaluatePartsCatalogPresentation } from "../../utils/partsCatalogUtils";
import { parseDtcAnswer } from "../../utils/dtcAnswerUtils";
import { parseGroupedSpecificationAnswer } from "../../utils/specSheetUtils";
import {
  ANSWER_COUNT_CHIP_SX,
  ANSWER_HEADING_SX,
  ANSWER_TITLE_SX,
} from "../../constants/ragUiConstants";
import { DtcResponseView } from "./DtcResponseView";
import { RagImageLightbox, type RagLightboxItem } from "./RagImageLightbox";
import { RagMarkdownContent } from "./RagMarkdownContent";
import { PartsCatalogView } from "./PartsCatalogView";
import { CatalogPostProcedureContent } from "./CatalogPostProcedureContent";
import { ProcedureCompactSection } from "./ProcedureCompactSection";
import { SpecSheetView } from "./SpecSheetView";
import { StepCard } from "./StepCard";
import { ImageCollage } from "./ImageCollage";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

/** Compact procedure phase header — heading + optional count chip (no divider). */
function ProcedureSectionHeader({
  title,
  countLabel,
}: {
  title: string;
  countLabel?: string;
}) {
  return (
    <Box
      sx={{
        mb: 1.75,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Typography
        component="h3"
        data-testid="procedure-section-title"
        sx={ANSWER_HEADING_SX}
      >
        {title}
      </Typography>
      {countLabel ? (
        <Box component="span" sx={ANSWER_COUNT_CHIP_SX}>
          {countLabel}
        </Box>
      ) : null}
    </Box>
  );
}

function VideoSourceNoticeCallout({ text }: { text: string }) {
  return (
    <Box
      sx={{
        mb: 1.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "rgba(0, 132, 244, 0.16)",
        bgcolor: "rgba(0, 132, 244, 0.06)",
      }}
    >
      <InfoOutlinedIcon
        sx={{ fontSize: 18, color: "primary.main", mt: "1px", flexShrink: 0 }}
      />
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.45,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function InlineFigure({
  item,
  page,
  onOpen,
  zoomLabel,
  pageLabel,
}: {
  item: RagImageItem;
  page: string;
  onOpen: (src: string) => void;
  zoomLabel: string;
  pageLabel: string;
}) {
  const label = (item.path || "diagram").split("/").pop() || "diagram";
  const src = item.path;
  if (!src) return null;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(src)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(src);
        }
      }}
      aria-label={`${zoomLabel} ${pageLabel}${page}`}
      title={label}
      sx={{
        my: 1.5,
        width: "100%",
        maxWidth: 480,
        textAlign: "left",
        p: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: "zoom-in",
        boxShadow: "0 1px 4px rgba(28,46,54,0.08)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(28,46,54,0.14)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          bgcolor: "#eef3f8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
      <Typography
        variant="labelSmall"
        sx={{
          px: 1.25,
          py: 0.6,
          bgcolor: "grey.50",
          color: "text.secondary",
          fontWeight: 600,
        }}
      >
        {pageLabel}
        {page}
      </Typography>
    </Box>
  );
}

function SpecMainDiagramCollage({
  image,
  pageLabel,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
  onOpen,
}: {
  image: RagImageItem | null;
  pageLabel: string;
  tapToOpenLabel: string;
  morePhotosLabel: (count: number) => string;
  zoomLabel: string;
  onOpen: (src: string, gallery: RagLightboxItem[]) => void;
}) {
  if (!image?.path) return null;
  const src = image.path;
  const gallery: RagLightboxItem[] = [
    { src, label: `${pageLabel}${image.metadata?.page ?? "?"}` },
  ];
  return (
    <Box sx={{ mt: 1.25 }}>
      <ImageCollage
        images={[{ src }]}
        onOpen={(opened) => onOpen(opened, gallery)}
        tapToOpenLabel={tapToOpenLabel}
        morePhotosLabel={morePhotosLabel}
        zoomLabel={zoomLabel}
        compactRoot
      />
    </Box>
  );
}

type LightboxState = {
  src: string;
  gallery: RagLightboxItem[];
  title?: string;
};

function stepImagesToGallery(
  bindings: StepImageBinding[],
  pageLabel: string
): RagLightboxItem[] {
  return bindings
    .filter(({ img }) => Boolean(img.path))
    .map(({ img, page }) => ({
      src: img.path,
      label: `${pageLabel}${page}`,
    }));
}

/** Prefer the first non-numbered preamble line as the section topic. */
function deriveStepsTopic(steps: ParsedStep[]): string {
  for (const step of steps) {
    if (step.num) continue;
    if (isVideoPrimaryProcedureNotice(step.text)) continue;
    const cleaned = stripInlineMarkdown(cleanStepDisplayText(step.text));
    if (cleaned) return cleaned.replace(/[:.!?]+$/, "").trim();
  }
  return "";
}

interface RagAnswerTextProps {
  text: string;
  inlineImages: Record<string, RagImageItem[]>;
  showRichMedia?: boolean;
  videos?: RagVideoItem[];
  userQuery?: string;
  /** Optional page alerts — used as fallback source for missing WI sections. */
  pageAlerts?: Record<string, RagAlert[]>;
  /** Main/inline diagram for specification (non-procedure, non-parts) answers. */
  specMainDiagram?: RagImageItem | null;
  /** Main exploded-view diagram for parts catalog. */
  partsMainDiagram?: RagImageItem | null;
  /** Additional diagrams below parts catalog (excludes main). */
  partsMoreDiagrams?: RagImageItem[];
  msgId?: string;
  procedure?: RagProcedure | null;
}

export function RagAnswerText({
  text,
  inlineImages,
  showRichMedia = true,
  videos = [],
  userQuery,
  pageAlerts,
  specMainDiagram = null,
  partsMainDiagram = null,
  partsMoreDiagrams = [],
  msgId = "msg",
  procedure = null,
}: RagAnswerTextProps) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [sectionScrollProgress, setSectionScrollProgress] = useState<Record<string, number>>(
    {}
  );
  const steps = parseNumberedSteps(text);
  const parsedSteps = steps ?? [];
  const payloadLayout = procedurePayloadIsLayoutSource(procedure);
  const hasSteps = payloadLayout || parsedSteps.some((s) => s.num);
  const numberedKind = useMemo(
    () => (steps ? classifyNumberedAnswerKind(steps) : null),
    [steps]
  );
  const partsPresentation = useMemo(
    () => evaluatePartsCatalogPresentation(userQuery, text, inlineImages),
    [userQuery, text, inlineImages]
  );
  const usePartsCatalog = partsPresentation.show;
  // Worker procedure payload is the layout contract when present.
  // Otherwise markdown WI parse / video-primary fallback.
  const useStepCards =
    (payloadLayout && !usePartsCatalog) ||
    shouldUseProcedureStepCards(steps, numberedKind, usePartsCatalog);
  const dtcAnswer = useMemo(() => parseDtcAnswer(text), [text]);
  const showDtcCard = Boolean(dtcAnswer) && !usePartsCatalog && !useStepCards;
  const specSheet = useMemo(() => {
    if (usePartsCatalog || useStepCards || showDtcCard) return null;
    return parseGroupedSpecificationAnswer(
      text,
      t("opva.chatAssistanceSpecCondition")
    );
  }, [text, usePartsCatalog, useStepCards, showDtcCard, t]);
  const pageLabel = t("opva.chatAssistancePageShort");
  const zoomLabel = t("opva.chatAssistanceClickToEnlarge");
  const tapToOpenLabel = t("opva.chatAssistanceTapToOpen");
  const morePhotosLabel = (count: number) => t("opva.chatAssistanceMorePhotos", { count });

  const openSpecMainImage = (src: string, gallery: RagLightboxItem[]) => {
    setLightbox({ src, gallery });
  };

  const onCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) {
      setScrollProgress(1);
      return;
    }
    setScrollProgress(Math.min(1, Math.max(0, el.scrollLeft / max)));
  }, []);

  const onSectionCarouselScroll = useCallback((sectionKey: string, el: HTMLDivElement) => {
    const max = el.scrollWidth - el.clientWidth;
    const next = max <= 0 ? 1 : Math.min(1, Math.max(0, el.scrollLeft / max));
    setSectionScrollProgress((prev) =>
      prev[sectionKey] === next ? prev : { ...prev, [sectionKey]: next }
    );
  }, []);

  const sectionProgressWidth = (sectionKey: string, stepCount: number) => {
    if (stepCount <= 1) return 100;
    const progress = sectionScrollProgress[sectionKey] ?? 0;
    return ((1 + progress * (stepCount - 1)) / stepCount) * 100;
  };

  const stepImagesByIndex = useMemo(() => {
    if (!hasSteps || !showRichMedia || !steps) return {} as Record<number, StepImageBinding[]>;
    return buildStepImagesByIndex(steps, inlineImages);
  }, [hasSteps, showRichMedia, steps, inlineImages]);

  const payloadDocument = useMemo(
    () => documentFromProcedurePayload(procedure, text),
    [procedure, text]
  );

  const procedureDocument = useMemo(() => {
    if (!useStepCards) return null;
    if (payloadDocument) return payloadDocument;
    return parseProcedureDocumentWithAlerts(text, pageAlerts);
  }, [useStepCards, payloadDocument, text, pageAlerts]);

  /** When WI merge returns null, still surface Tools recovered from pageAlerts. */
  const alertFallbackCompacts = useMemo(() => {
    if (!useStepCards || procedureDocument) return [] as ProcedureBlock[];
    return getAlertFallbackCompactBlocks(text, pageAlerts);
  }, [useStepCards, procedureDocument, text, pageAlerts]);

  const displayProcedureBlocks = useMemo(() => {
    const base = procedureDocument
      ? procedureBlocksForDisplay(procedureDocument.blocks)
      : alertFallbackCompacts;
    if (payloadDocument) return base;
    return attachProcedureStepVideos(base, procedure);
  }, [procedureDocument, alertFallbackCompacts, procedure, payloadDocument]);

  const { toolsBlocks, bodyBlocks } = useMemo(
    () => splitProcedureBlocksForDisplay(displayProcedureBlocks),
    [displayProcedureBlocks]
  );

  /**
   * Count chip belongs on the heading that labels the carousel. An umbrella
   * documentTitle only gets it when the sole steps block has no title of its
   * own (ATX / video-primary). Named phases (Removing / Installing) render
   * the chip on ProcedureSectionHeader instead.
   */
  const documentTitleStepCount = useMemo(() => {
    const heading = procedureDocument?.documentTitle;
    if (!heading) return 0;
    const stepBlocks = bodyBlocks.filter((b) => b.kind === "steps");
    if (stepBlocks.length !== 1) return 0;
    if (stepBlocks[0]!.title) return 0;
    return stepBlocks[0]!.items.filter((it) => it.num).length;
  }, [procedureDocument, bodyBlocks]);

  /**
   * WI Step Cards: bind images from WI item text (`[p.N]`), not legacy parseNumberedSteps
   * indices. Flatten all steps-blocks into one pass so Removing `1.` and Installation
   * `1.` do not share proportional leftovers.
   */
  const wiStepImagesByBlock = useMemo(() => {
    const empty = new Map<ProcedureBlock, Record<number, StepImageBinding[]>>();
    if (!showRichMedia || !procedureDocument) return empty;

    const flat: Array<{
      block: ProcedureBlock;
      itemIdx: number;
      step: ParsedStep;
    }> = [];

    for (const block of displayProcedureBlocks) {
      if (block.kind !== "steps") continue;
      block.items.forEach((item, itemIdx) => {
        if (!item.num) return;
        flat.push({
          block,
          itemIdx,
          step: { num: item.num, text: item.text, marker: "dot" },
        });
      });
    }

    if (!flat.length) return empty;

    const bindings = buildStepImagesByIndex(
      flat.map((f) => f.step),
      inlineImages
    );

    const map = new Map<ProcedureBlock, Record<number, StepImageBinding[]>>();
    flat.forEach((f, flatIdx) => {
      let perBlock = map.get(f.block);
      if (!perBlock) {
        perBlock = {};
        map.set(f.block, perBlock);
      }
      perBlock[f.itemIdx] = bindings[flatIdx] ?? [];
    });
    return map;
  }, [showRichMedia, procedureDocument, displayProcedureBlocks, inlineImages]);

  const openStepImage = (
    bindings: StepImageBinding[],
    src: string,
    title?: string
  ) => {
    setLightbox({
      src,
      gallery: stepImagesToGallery(bindings, pageLabel),
      title,
    });
  };

  if (showDtcCard && dtcAnswer) {
    return (
      <>
        <DtcResponseView data={dtcAnswer} />
        <RagImageLightbox
          src={lightbox?.src ?? null}
          gallery={lightbox?.gallery}
          title={lightbox?.title}
          onClose={() => setLightbox(null)}
        />
      </>
    );
  }

  if (!hasSteps) {
    const segments = parseSegments(text);
    const prose = text.replace(/\[p\.\d+\]/g, "").replace(/\[img:\d+\]\s*/g, "");
    const { notice: videoSourceNotice, body: answerBody } =
      splitVideoSourceProvenanceNotice(prose);
    const showInlineFigures =
      showRichMedia && !specMainDiagram?.path;
    return (
      <Box>
        {videoSourceNotice ? (
          <VideoSourceNoticeCallout text={videoSourceNotice} />
        ) : null}
        {specSheet ? (
          <SpecSheetView sheet={specSheet} />
        ) : (
          <RagMarkdownContent
            text={stripDisplayedVideoCitations(answerBody || prose, videos)}
          />
        )}
        {showRichMedia && (
          <SpecMainDiagramCollage
            image={specMainDiagram}
            pageLabel={pageLabel}
            tapToOpenLabel={tapToOpenLabel}
            morePhotosLabel={morePhotosLabel}
            zoomLabel={zoomLabel}
            onOpen={openSpecMainImage}
          />
        )}
        {showInlineFigures &&
          segments
            .filter((s) => s.page && inlineImages[s.page]?.length)
            .map((seg, i) =>
              (inlineImages[seg.page!] ?? []).map((img, j) => (
                <InlineFigure
                  key={`${i}-${j}`}
                  item={img}
                  page={seg.page!}
                  onOpen={(src) => setLightbox({ src, gallery: [{ src }] })}
                  pageLabel={pageLabel}
                  zoomLabel={zoomLabel}
                />
              ))
            )}
        <RagImageLightbox
          src={lightbox?.src ?? null}
          gallery={lightbox?.gallery}
          title={lightbox?.title}
          onClose={() => setLightbox(null)}
        />
      </Box>
    );
  }

  if (usePartsCatalog) {
    const {
      introMarkdown,
      catalogHeadingMarkdown,
      postMarkdown,
    } = partsPresentation;
    return (
      <>
        <Box>
          {introMarkdown ? (
            <CatalogPostProcedureContent
              text={introMarkdown}
              inlineImages={inlineImages}
              videos={videos}
              showRichMedia={showRichMedia}
            />
          ) : null}
          {catalogHeadingMarkdown ? (
            <RagMarkdownContent
              text={cleanStepDisplayText(catalogHeadingMarkdown)}
            />
          ) : null}
          <PartsCatalogView
            entries={partsPresentation.entries}
            mainDiagram={partsMainDiagram}
            moreDiagrams={partsMoreDiagrams}
            msgId={msgId}
            pageLabel={pageLabel}
          />
          {postMarkdown ? (
            <CatalogPostProcedureContent
              text={postMarkdown}
              inlineImages={inlineImages}
              videos={videos}
              showRichMedia={showRichMedia}
            />
          ) : null}
        </Box>
      </>
    );
  }

  if (!useStepCards) {
    const prose = text.replace(/\[p\.\d+\]/g, "").replace(/\[img:\d+\]\s*/g, "");
    const { notice: videoSourceNotice, body: answerBody } =
      splitVideoSourceProvenanceNotice(prose);
    return (
      <>
        <Box>
          {videoSourceNotice ? (
            <VideoSourceNoticeCallout text={videoSourceNotice} />
          ) : null}
          {specSheet ? (
            <SpecSheetView sheet={specSheet} />
          ) : (
            <RagMarkdownContent
            text={stripDisplayedVideoCitations(answerBody || prose, videos)}
          />
          )}
          {showRichMedia && (
            <SpecMainDiagramCollage
              image={specMainDiagram}
              pageLabel={pageLabel}
              tapToOpenLabel={tapToOpenLabel}
              morePhotosLabel={morePhotosLabel}
              zoomLabel={zoomLabel}
              onOpen={openSpecMainImage}
            />
          )}
        </Box>
        <RagImageLightbox
          src={lightbox?.src ?? null}
          gallery={lightbox?.gallery}
          title={lightbox?.title}
          onClose={() => setLightbox(null)}
        />
      </>
    );
  }

  const numberedCount = parsedSteps.filter((s) => s.num).length;
  const topic = deriveStepsTopic(parsedSteps);
  const videoPrimaryNotice = extractVideoPrimaryProcedureNotice(parsedSteps);
  const procedureSections = groupProcedureSections(parsedSteps);
  const hasTitledSections = procedureSections.some((s) => Boolean(s.title));
  const procedureIntro = procedureDocument
    ? procedureDocument.intro
    : extractProcedureIntroProse(
        parsedSteps,
        procedureSections.map((s) => s.title)
      );
  const sectionTitle = !shouldShowStandaloneProcedureStepHeading(
    hasTitledSections || Boolean(procedureDocument),
    videoPrimaryNotice
  )
    ? null
    : topic
      ? t("opva.chatAssistanceStepsWithTopic", { topic, count: numberedCount })
      : t("opva.chatAssistanceStepsGeneric", { count: numberedCount });
  const progressWidth =
    numberedCount <= 1
      ? 100
      : ((1 + scrollProgress * (numberedCount - 1)) / numberedCount) * 100;

  const firstStepsBlockIndex = bodyBlocks.findIndex((b) => b.kind === "steps");
  const hasStepCarouselContent =
    firstStepsBlockIndex >= 0 || (!procedureDocument && numberedCount > 0);

  const renderStepsHeading = () => {
    if (!hasStepCarouselContent || !sectionTitle) return null;
    return (
      <ProcedureSectionHeader title={sectionTitle} />
    );
  };

  const renderStepCardFromItem = (
    item: ProcedureBlockItem,
    key: string,
    images: StepImageBinding[] = []
  ) => {
    const { title: rawTitle, body: rawBody } = splitStepTitleBody(item.text);
    const cited = extractStepVideoReferences(
      [rawTitle, rawBody].filter(Boolean).join(" "),
      videos
    );
    const stepVideos = item.videos?.length ? item.videos : cited.videos;
    const title = stripStepVideoReferences(rawTitle);
    const bodyNoVideos = stripStepVideoReferences(rawBody);
    const { text: body, link: actionLink } = extractStepActionLink(bodyNoVideos);

    return (
      <Box
        key={key}
        sx={{
          flex: "0 0 auto",
          // Use more of the response width on desktop; keep mobile swipe-friendly.
          width: { xs: 280, sm: 320, md: 360 },
          maxWidth: { xs: "85vw", md: "100%" },
          scrollSnapAlign: "start",
          alignSelf: "stretch",
          display: "flex",
        }}
      >
        <StepCard
          title={title}
          body={body}
          videos={stepVideos}
          actionLink={actionLink}
          images={images}
          onOpenImage={(src: string) =>
            openStepImage(
              images,
              src,
              [t("opva.chatAssistanceStepLabel", { num: item.num }), title]
                .filter(Boolean)
                .join(" · ")
            )
          }
          stepLabel={t("opva.chatAssistanceStepLabel", { num: item.num })}
          tapToOpenLabel={t("opva.chatAssistanceTapToOpen")}
          morePhotosLabel={(count: number) =>
            t("opva.chatAssistanceMorePhotos", { count })
          }
          zoomLabel={zoomLabel}
        />
      </Box>
    );
  };

  const renderStepsCarousel = (
    sectionKey: string,
    title: string | null,
    stepItems: Array<{ item: ProcedureBlockItem; key: string; images?: StepImageBinding[] }>,
    attachScrollTracker: boolean,
    isFirstSection: boolean,
    showSectionChrome: boolean
  ) => {
    if (!stepItems.length) return null;
    const stepCount = stepItems.length;

    return (
      <Box key={sectionKey} sx={{ mt: !isFirstSection ? 2.25 : 0 }}>
        {showSectionChrome && title && (
          <ProcedureSectionHeader
            title={title}
            countLabel={
              stepCount > 1
                ? t("opva.chatAssistanceStepsGeneric", { count: stepCount })
                : undefined
            }
          />
        )}
        <Box
          ref={attachScrollTracker ? carouselRef : undefined}
          onScroll={
            attachScrollTracker
              ? onCarouselScroll
              : showSectionChrome && stepCount > 1
                ? (e) => onSectionCarouselScroll(sectionKey, e.currentTarget)
                : undefined
          }
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            mx: { xs: -0.5, md: 0 },
            px: { xs: 0.5, md: 0 },
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "grey.300",
              borderRadius: 3,
            },
          }}
        >
          {stepItems.map(({ item, key, images }) =>
            renderStepCardFromItem(item, key, images)
          )}
        </Box>
        {showSectionChrome && stepCount > 1 && (
          <Box sx={{ mt: 1.25 }}>
            <Box
              sx={{
                height: 3,
                borderRadius: 999,
                bgcolor: "grey.200",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${sectionProgressWidth(sectionKey, stepCount)}%`,
                  bgcolor: "primary.main",
                  borderRadius: 999,
                  transition: "width 0.15s ease-out",
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderSectionCarousel = (
    section: ProcedureSection,
    sectionKey: string,
    attachScrollTracker: boolean,
    isFirstSection: boolean
  ) => {
    const sectionSteps = section.stepIndices
      .map((idx) => ({ step: parsedSteps[idx]!, idx }))
      .filter(({ step }) => Boolean(step.num));
    if (!sectionSteps.length) return null;

    const boundItems = bindProcedureClipsToNumberedItems(
      sectionSteps.map(({ step }) => ({ num: step.num, text: step.text })),
      procedure
    );

    return renderStepsCarousel(
      sectionKey,
      section.title,
      sectionSteps.map(({ idx }, i) => ({
        item: boundItems[i]!,
        key: `legacy-${idx}`,
        images: showRichMedia ? stepImagesByIndex[idx] ?? [] : [],
      })),
      attachScrollTracker,
      isFirstSection,
      hasTitledSections
    );
  };

  const renderProcedureBlock = (
    block: ProcedureBlock,
    index: number,
    hideOwnTitle = false
  ) => {
    if (block.kind === "compact") {
      return (
        <Box key={`compact-${index}`} sx={{ mt: index === 0 ? 0 : 1.5 }}>
          <ProcedureCompactSection
            title={block.title}
            variant={block.compactVariant ?? "prose"}
            body={stripDisplayedVideoCitations(block.body, videos)}
            items={block.items.map((item) => ({
              ...item,
              text: stripDisplayedVideoCitations(item.text, videos),
            }))}
            inlineImages={showRichMedia ? inlineImages : undefined}
            onOpenImage={(src, gallery) =>
              openStepImage(gallery, src, block.title)
            }
            tapToOpenLabel={tapToOpenLabel}
            morePhotosLabel={morePhotosLabel}
            zoomLabel={zoomLabel}
          />
        </Box>
      );
    }

    const blockImages = showRichMedia ? wiStepImagesByBlock.get(block) : undefined;

    return renderStepsCarousel(
      `wi-steps-${index}`,
      hideOwnTitle ? null : block.title,
      block.items.map((item, i) => ({
        item,
        key: `wi-${index}-${i}`,
        images: blockImages?.[i] ?? [],
      })),
      false,
      index === 0,
      !hideOwnTitle
    );
  };

  return (
    <>
      {/* Full-width within the outer assistant bubble (no centered max-width band). */}
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {videoPrimaryNotice ? (
          <VideoSourceNoticeCallout text={videoPrimaryNotice} />
        ) : null}

        {/* Tools sits under the top Safety notices rail. */}
        {toolsBlocks.map((block, i) => renderProcedureBlock(block, i))}

        {procedureDocument?.documentTitle ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mt: toolsBlocks.length ? 1.5 : 0,
              mb: 1.5,
              minWidth: 0,
            }}
          >
            <Typography
              component="h2"
              data-testid="answer-title"
              sx={ANSWER_TITLE_SX}
            >
              {procedureDocument.documentTitle}
            </Typography>
            {documentTitleStepCount > 1 ? (
              <Box component="span" sx={ANSWER_COUNT_CHIP_SX}>
                {t("opva.chatAssistanceStepsGeneric", {
                  count: documentTitleStepCount,
                })}
              </Box>
            ) : null}
          </Box>
        ) : null}

        {procedureIntro ? (
          <Box
            sx={{
              mt: toolsBlocks.length ? 1.5 : 0,
              mb: 1.5,
              color: "text.secondary",
              typography: "bodySmall",
              fontWeight: 400,
              lineHeight: 1.55,
              "& p": { m: 0, mb: 0.75, color: "inherit" },
              "& p:last-child": { mb: 0 },
            }}
          >
            <RagMarkdownContent
              text={stripDisplayedVideoCitations(procedureIntro, videos)}
              dense
            />
          </Box>
        ) : null}

        {procedureDocument
          ? bodyBlocks.map((block, i) => {
              const prevGroup = i > 0 ? bodyBlocks[i - 1]!.groupTitle : null;
              const prevNested = i > 0 ? bodyBlocks[i - 1]!.nestedTitle : null;
              const showGroupTitle =
                Boolean(block.groupTitle) &&
                block.groupTitle !== prevGroup &&
                block.groupTitle !== procedureDocument.documentTitle &&
                block.groupTitle !== block.title;
              const showNestedTitle =
                Boolean(block.nestedTitle) &&
                block.nestedTitle !== prevNested &&
                block.nestedTitle !== block.groupTitle;
              const hideOwnTitle =
                Boolean(block.nestedTitle) && block.nestedTitle === block.title;
              return (
                <Fragment key={`proc-body-${i}`}>
                  {showGroupTitle ? (
                    <Typography
                      component="h2"
                      data-testid="answer-title"
                      sx={{
                        ...ANSWER_TITLE_SX,
                        mt:
                          toolsBlocks.length ||
                          Boolean(procedureDocument.documentTitle) ||
                          Boolean(procedureIntro) ||
                          i > 0
                            ? 1.5
                            : 0,
                        mb: showNestedTitle ? 1 : 1.5,
                      }}
                    >
                      {block.groupTitle}
                    </Typography>
                  ) : null}
                  {showNestedTitle ? (
                    <Typography
                      component="h3"
                      data-testid="answer-nested-title"
                      sx={{
                        ...ANSWER_HEADING_SX,
                        mt: showGroupTitle ? 0 : 1.5,
                        mb: 1.5,
                      }}
                    >
                      {block.nestedTitle}
                    </Typography>
                  ) : null}
                  {i === firstStepsBlockIndex ? renderStepsHeading() : null}
                  {renderProcedureBlock(
                    block,
                    toolsBlocks.length + i,
                    hideOwnTitle
                  )}
                </Fragment>
              );
            })
          : (
            <>
              {bodyBlocks.map((block, i) => (
                <Fragment key={`legacy-body-${i}`}>
                  {i === firstStepsBlockIndex ? renderStepsHeading() : null}
                  {renderProcedureBlock(block, toolsBlocks.length + i)}
                </Fragment>
              ))}
              {firstStepsBlockIndex < 0 ? renderStepsHeading() : null}
              {procedureSections.map((section, i) =>
                renderSectionCarousel(
                  section,
                  `proc-section-${i}`,
                  !hasTitledSections && procedureSections.length === 1 && i === 0,
                  i === 0 && toolsBlocks.length === 0 && bodyBlocks.length === 0
                )
              )}
            </>
          )}

        {!procedureDocument && !hasTitledSections && numberedCount > 1 && (
          <Box sx={{ mt: 1.75 }}>
            <Box
              sx={{
                height: 3,
                borderRadius: 999,
                bgcolor: "grey.200",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progressWidth}%`,
                  bgcolor: "primary.main",
                  borderRadius: 999,
                  transition: "width 0.15s ease-out",
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
      <RagImageLightbox
        src={lightbox?.src ?? null}
        gallery={lightbox?.gallery}
        title={lightbox?.title}
        onClose={() => setLightbox(null)}
      />
    </>
  );
}
